import prepareTranslate from '@/helpers/dictionary';
import { useForm } from '@mantine/form';
import {
  Button,
  Grid,
  NumberInput,
  Select,
  TextInput,
} from '@mantine/core';
import { useState } from 'react';
import { ACCORD_TYPE, PRODUCT_ENTITY } from '@/helpers/enums';
import DetailsModal from '@/components/DetailsModal';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import validations from '@/helpers/validation';
import Modal from '@/components/Modal/Index';
import { useDisclosure } from '@mantine/hooks';
import PropTypes from 'prop-types';
import agentMutate from '@/api/mutitations/agent';

function AgentClientCreate(props) {
  const {
    open,
    close,
    opened,
    setKey,
    id,
  } = props;

  const t = prepareTranslate();
  const [actionResultOpended,
    { open: actionResultOpen, close: actionResultClose },
  ] = useDisclosure();

  const { data: companiesData } = companyQuery.useGetAll();
  const { data: productsData } = productQuery.useGetAll();
  const [loading, setLoading] = useState(false);
  const [resultLabel, setResultLabel] = useState('');

  const form = useForm({
    initialValues: {
      accordType: 'independent',
    },
    validate: {
      companyId: (value) => validations.required(value),
      uid: (value) => validations.required(value),
      accordType: (value) => validations.required(value),
      branchId: (value) => validations.required(value),
      clientNumber: (value) => validations.required(value),
    },
  });

  const companiesOptions = companiesData ? (
    companiesData.map((company) => ({
      label: company.hebrew,
      value: company._id,
    }))) : (
    []
  );

  // eslint-disable-next-line max-len
  const clientProducts = productsData?.filter((product) => product.applicableTo.includes(PRODUCT_ENTITY.client));
  const productsOptions = clientProducts?.map((product) => ({
    label: product.hebrew,
    value: product._id,
  }));

  const { mutateAsync: addAgreementMutate } = agentMutate.usePushToArray();

  const handleClientSave = async (values) => {
    setLoading(true);

    try {
      const clonedValues = { ...values, status: 'active' };

      const data = {
        value: { ...clonedValues },
        id,
        key: 'clients',
      };
      const newAgreement = await addAgreementMutate(data);

      if (!newAgreement) {
        setResultLabel(t('pages.agentEdit.notification.failure.title'));
        actionResultOpen();
        return;
      }

      setResultLabel(t('components.createClients.success'));
      actionResultOpen();
      form.reset();
      setKey((prev) => prev + 1);
      close();
    } catch (error) {
      setResultLabel(t('pages.agentEdit.notification.failure.title'));
      actionResultOpen();
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    form.reset();
    form.clearErrors();
    close();
  };

  const actions = [
    <Button
      key='primary'
      loading={loading}
      onClick={form.onSubmit(handleClientSave)}
      w={'7rem'}
    >
      {t('global.update')}
    </Button>,
    <Button
      key='cancel'
      variant='outline'
      onClick={handleCancelClick}
      w={'7rem'}>
      {t('global.cancel')}
    </Button>,
  ];

  const resultActions = [
    <Button
      key='clseButton' onClick={() => actionResultClose()}>
      {t('global.close')}
    </Button>,
  ];

  const clientAccordType = form.getValues().accordType;

  return (
    <>
      <DetailsModal
        actions={actions}
        open={open}
        close={handleCancelClick}
        opened = {opened}
        label={t('components.editClients.addClient')}
          >
     <Grid gutter={{ base: 'xl' }} >
              <Grid.Col span={{ base: 3 }}>
                <Select
                  label={t('components.editClients.company')}
                  key={form.key('companyId')}
                  data={companiesOptions}
                  required
                  {...form.getInputProps('companyId')}
                />
                <TextInput
                  autoComplete='off'
                  maxLength={25}
                  required
                  label={t('components.editClients.uid')}
                  key={form.key('uid')}
                  {...form.getInputProps('uid')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3 }}>
              <Select
                    required
                    label={t('components.editClients.branch')}
                    data={productsOptions}
                    key={form.key('branchId')}
                    {...form.getInputProps('branchId')}
            />
            <Select
                required
                label={t('components.editClients.accordType')}
                key={form.key('accordType')}
                defaultValue={ACCORD_TYPE[1].value}
                data={ACCORD_TYPE}
                {...form.getInputProps('accordType')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 3 }}>
              <TextInput
                  autoComplete='off'
                  label={t('components.editClients.number')}
                  key={form.key('clientNumber')}
                  maxLength={7}
                  required
                  {...form.getInputProps('clientNumber')}
                      />
            {
                  clientAccordType === 'independent' ? (
                  <NumberInput
                    autoComplete='off'
                    label={t('components.editClients.commission')}
                    key={form.key('agentCommission')}
                    maxLength={7}
                    {...form.getInputProps('agentCommission')}
                  />
                  ) : (
                  <NumberInput
                    autoComplete='off'
                    label={t('components.editClients.commission')}
                    key={form.key('employeeAgentCommission')}
                    maxLength={7}
                    {...form.getInputProps('employeeAgentCommission')}
                  />
                  )
                }
                  </Grid.Col>
                  <Grid.Col span={{ base: 3 }}>
               <TextInput
                label={t('components.editClients.clientName')}
                key={form.key('clientName')}
                {...form.getInputProps('clientName')}
             />
                  </Grid.Col>
              </Grid>
              <Grid>
                  <Grid.Col>
                      <TextInput
                        label={t('components.editClients.comments')}
                        key={form.key('comments')}
                        maxLength={7}
                        {...form.getInputProps('comments')}
                      />
                  </Grid.Col>
              </Grid>
      </DetailsModal>
      <Modal
        opened={actionResultOpended}
        onClose={actionResultClose}
        actions={resultActions}
        label={resultLabel}
      />
    </>
  );
}

AgentClientCreate.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  setKey: PropTypes.func,
  id: PropTypes.string,
};

export default AgentClientCreate;
